import { useState, useEffect, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { OptImg } from '@/components/marketing/OptImg';

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 8,
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--ink-2)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid var(--line)',
  background: '#fff',
  fontSize: 15,
  color: 'var(--ink)',
  outline: 'none',
  transition: 'border-color 0.15s',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const EARLY_ACCESS_URL =
  'https://bqy52me7g9.execute-api.eu-west-2.amazonaws.com/Prod/early-access';

export default function GetStartedPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [crm, setCrm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.title = 'Apply for a founder slot · Foreman';
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (path: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(path);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !crm) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(EARLY_ACCESS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), crm }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong');
      }
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Nav (mirrors new landing, Sign in hidden until launch) */}
      <nav className="nav" id="nav" data-scrolled={scrolled ? 'true' : 'false'}>
        <div className="wrap nav-inner">
          <a href="/" onClick={goTo('/')} className="brand">
            <OptImg
              src="/foreman-ui/foreman-logo.png"
              widths={[60, 120]}
              alt="Foreman logo"
              width={30}
              height={30}
              loading="eager"
              fetchPriority="high"
            />
            <span>Foreman</span>
          </a>
          <div className="nav-links">
            <a href="/articles" onClick={goTo('/articles')}>
              Articles
            </a>
          </div>
        </div>
      </nav>

      <main style={{ background: 'var(--bg)', minHeight: '100vh' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 110,
            paddingBottom: 80,
            paddingLeft: 24,
            paddingRight: 24,
            minHeight: 'calc(100vh - 70px)',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 480,
              background: 'var(--surface)',
              border: '1px solid var(--line-2)',
              borderRadius: 14,
              padding: '44px 40px',
              boxShadow: '0 8px 32px rgba(20,28,16,0.06)',
            }}
          >
            <div className="eyebrow">Founder cohort</div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 30,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                color: 'var(--ink)',
                margin: '14px 0 8px',
                lineHeight: 1.1,
              }}
            >
              Apply for a slot.
            </h1>
            <p
              style={{
                fontSize: 15,
                color: 'var(--ink-3)',
                margin: '0 0 32px',
                lineHeight: 1.5,
              }}
            >
              Next batch open. Fourteen days free. No commitment.
            </p>

            {submitted ? (
              <div style={{ paddingTop: 8 }}>
                <h2
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 22,
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                    color: 'var(--ink)',
                    margin: '0 0 10px',
                  }}
                >
                  Application received.
                </h2>
                <p
                  style={{
                    fontSize: 14,
                    color: 'var(--ink-3)',
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  You'll hear back within 48 hours. If you need anything sooner,{' '}
                  <a
                    href="mailto:owen@askforeman.co.uk"
                    style={{
                      color: 'var(--ink-2)',
                      fontWeight: 500,
                      textDecoration: 'underline',
                    }}
                  >
                    owen@askforeman.co.uk
                  </a>
                  .
                </p>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: 18 }}>
                    <label htmlFor="gs-email" style={labelStyle}>
                      Work email
                    </label>
                    <input
                      id="gs-email"
                      type="email"
                      required
                      placeholder="you@yourcompany.com"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ marginBottom: 26 }}>
                    <label htmlFor="gs-crm" style={labelStyle}>
                      Which CRM do you run?
                    </label>
                    <select
                      id="gs-crm"
                      required
                      value={crm}
                      onChange={(e) => setCrm(e.target.value)}
                      style={{
                        ...inputStyle,
                        appearance: 'none',
                        color: crm ? 'var(--ink)' : 'var(--muted, #8a8e84)',
                      }}
                    >
                      <option value="" disabled>
                        Select your CRM
                      </option>
                      <option value="Simpro">Simpro</option>
                      <option value="HubSpot">HubSpot</option>
                      <option value="Fergus">Fergus</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {error && (
                    <p
                      style={{
                        color: '#b1422a',
                        fontSize: 13,
                        marginBottom: 14,
                      }}
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary"
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      opacity: submitting ? 0.7 : 1,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {submitting ? 'Sending…' : 'Apply for a founder slot'}
                  </button>
                </form>

                <p
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--muted, #8a8e84)',
                    textAlign: 'center',
                    marginTop: 18,
                  }}
                >
                  No card. No commitment.
                </p>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer (mirrors new landing, Sign in hidden until launch) */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <OptImg
              src="/foreman-ui/foreman-logo.png"
              widths={[60, 120]}
              alt="Foreman"
            />
            <span>Foreman</span>
          </div>
          <div className="footer-links">
            <a href="/privacy" onClick={goTo('/privacy')}>
              Privacy
            </a>
            <a href="#">Security</a>
            <a href="#">Contact</a>
            <a href="/articles" onClick={goTo('/articles')}>
              Articles
            </a>
          </div>
          <div className="footer-meta">© 2026 · Built for the trades</div>
        </div>
      </footer>
    </>
  );
}
